/* @flow */
import React, { useContext } from "react";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import Gettext from "../common/Gettext";
import RawScrollable from "../common/Scrollable";
import { withFetch } from "../common/fetch";
import { DealsIcon, TradingHistoryIcon } from "./Icons";
import { CastDown, CastUp } from "./Buttons";
import CollapsibleSection from "./CollapsibleSection";
import TradeHistory, { OpenOperation, ClosedOperation } from "./History";
import TradeSettings from "./Settings";
import { Fade } from "../common/Animations";
import { ModalConsumer } from "../common/ModalProvider";
import OperationCheque from "../Trade/OperationCheque";
import FeatureFlags from "../../FeatureFlags";

import type { Dispatch } from "../../redux/action";
import type { Deal, StockPoint } from "../../redux/CommonTypes";
import type { State as ReduxState } from "../../redux/reducer";

import HistoryPopup from "../../components/HistoryPopup/HistoryPopup";
import { gaReducer } from "../GA";
import Tour, { TourContext } from "../Tour";
import ProtectionInfo from "../Protection/ProtectionInfo";

const Wrapper = styled.div`
  @media (min-width: 960px) {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: ${props => props.theme.mainLight};
`;

// const CollapsibleWrapper = styled.div`
//   display: flex;
//   flex-direction: column;
//   flex: 1;
//   background-color: ${props => props.theme.mainDark};
// `;

const Scrollable = styled(RawScrollable)`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${props => props.theme.dealsBackground};
`;

const CollapsibleContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 18px;
  padding-bottom: 18px;
  text-align: center;
  border-bottom: 1px dashed ${props => props.theme.borderMainColor};
`;

const ButtonsInner = styled.div`
  display: flex;
  flex-direction: column;

  ${({ tourBorder }) =>
    tourBorder &&
    css`
      margin-top: -5px;
      margin-bottom: -5px;
      padding: 5px;
      border-radius: 4px;
      ${tourBorder.border};
    `};

  ${({ tourBorderAnimation }) =>
    tourBorderAnimation &&
    tourBorderAnimation.animation(tourBorderAnimation.keyframe)};
`;

const HistoryButton = styled.button`
  display: flex;
  align-items: center;
  height: 43px;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  color: #96929a;
  text-align: left;
  background-color: ${({ theme }) => theme.mainLight};
  border: none;
  cursor: pointer;
  transition: all 0.35s;
  direction: ${({ theme }) => theme.direction === "rtl" && "rtl"};

  :hover,
  :focus,
  :active {
    color: ${({ theme }) => theme.accent};
  }
`;

const HistoryIcon = styled.div`
  min-width: 23px;
  width: 23px;
  height: 24px;
  margin-left: 8px;
  margin-right: 6px;
  color: #99959d;
  transition: all 0.35s;
`;

const StockClose = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  opacity: 0.73;
  background-color: ${props => props.theme.main};
`;

type RemoteTradeSettings = $PropertyType<ReduxState, "remoteTradeSettings">;
type Settings = $PropertyType<ReduxState, "settings">;

type Props = {
  activeAccount: Object,
  point: StockPoint,
  pointsByStock: Object | void,
  deals: $PropertyType<ReduxState, "deals">,
  suggestedBids: $PropertyType<RemoteTradeSettings, "bids">,
  percentBids: $PropertyType<RemoteTradeSettings, "percentBids">,
  percentBidThreshold?: number,
  possibleIntervals: $PropertyType<RemoteTradeSettings, "intervals">,
  currentBid: $PropertyType<Settings, "currentBid">,
  currentInterval: $PropertyType<Settings, "currentInterval">,
  isDealAmountValid: boolean,
  validateDealAmount: (key: boolean) => void,
  now: number,
  maxBidVolume: number,
  dispatch: Dispatch,
  accountType: "demo" | "real",
  onSubmit: (deal: Deal) => void,
  onIntentUpdate: (nextIntent: any) => void,
  showIncreaseBidLimitMessage: () => void,
  gettext: (strings: Array<string>, ...values: any) => string,
  disabled: boolean,
  tradeMode: "binary" | "tournament"
};

export let ArtificialTourDeals = props => {
  let tour = useContext(TourContext);

  return tour.deals.map(deal => {
    if (tour.activeDeal === deal) {
      let present = tour.points[tour.points.length - 1];
      return (
        <OpenOperation
          {...deal}
          tradeMode="binary"
          mobile={props.mobile}
          now={present.date}
          pointsByStock={{}}
        />
      );
    } else {
      return (
        <ClosedOperation
          {...deal}
          tradeMode="binary"
          mobile={props.mobile}
          profit={deal.amount * 0.9}
        />
      );
    }
  });
};

class TradeControls extends React.Component<Props, {}> {
  state = {
    popupIsOpen: false,
    tourInterval: 30
  };

  openPopup = () => this.setState({ popupIsOpen: true });
  closePopup = () => this.setState({ popupIsOpen: false });

  handleSettingsUpdate = (nextSettings: *) => {
    this.props.dispatch({
      type: "UPDATE_SETTINGS",
      payload: nextSettings
    });
  };

  shouldComponentUpdate(nextProps, nextState) {
    const isThereAnyOpenDeals =
      nextProps.deals && nextProps.deals.some(deal => deal.time != null);

    const isSomethingValuableChanged =
      nextProps.disabled !== this.props.disabled ||
      nextProps.currentBid !== this.props.currentBid ||
      nextProps.possibleIntervals !== this.props.possibleIntervals ||
      nextProps.suggestedBids !== this.props.suggestedBids ||
      nextProps.percentBids !== this.props.percentBids ||
      nextProps.accountType !== this.props.accountType ||
      nextProps.currentInterval !== this.props.currentInterval ||
      nextProps.deals !== this.props.deals ||
      nextState.tourInterval !== this.state.tourInterval ||
      nextProps.compensationUpdated !== this.props.compensationUpdated ||
      nextProps.compensationCreated !== this.props.compensationCreated;
    return isThereAnyOpenDeals || isSomethingValuableChanged;
  }

  _submit(type: "UP" | "DOWN") {
    const { currentBid, currentInterval, point, accountType } = this.props;

    if (!point) {
      return; // Или может throw?
    }

    type === "UP"
      ? gaReducer("CLICKBTN_UPPER_BET")
      : gaReducer("CLICKBTN_LOWER_BET");

    this.props.onSubmit({
      id: null,
      time: 1337,
      percent: null,
      bidType: type === "UP",
      amount: currentBid,
      bTime: currentInterval,
      stock: point.stock,
      value: point.value,
      accountType,
      timestamp: Number(point.date / 1000),
      open_timestamp: Number(point.date / 1000),
      date: point.date
    });
  }

  original = React.createRef(null);
  originalDeals = React.createRef(null);

  render() {
    const {
      activeAccount,
      deals,
      percentBids,
      percentBidThreshold,
      suggestedBids,
      possibleIntervals,
      currentBid,
      currentInterval,
      showIncreaseBidLimitMessage,
      isDealAmountValid,
      validateDealAmount,
      tradeMode,
      dealsToCompensate,
      compensationUpdated,
      compensationCreated,
      onCompensationCreated
    } = this.props;

    let { tourInterval } = this.state;

    return (
      <Container>
        <Wrapper>
          {tradeMode === "binary" && (
            <ProtectionInfo
              isDesktop={true}
              dealsToCompensate={dealsToCompensate}
              compensationUpdated={compensationUpdated}
              compensationCreated={compensationCreated}
              onCompensationCreated={onCompensationCreated}
              activeAccount={activeAccount}
            />
          )}
          {this.props.disabled && <StockClose />}
          <TradeSettings
            tradeMode={this.props.tradeMode}
            activeAccount={activeAccount}
            suggestedBids={suggestedBids}
            percentBids={percentBids}
            percentBidThreshold={percentBidThreshold}
            possibleIntervals={possibleIntervals}
            currentBid={currentBid}
            currentInterval={currentInterval}
            onSettingsUpdate={this.handleSettingsUpdate}
            maxBidVolume={this.props.maxBidVolume}
            showIncreaseBidLimitMessage={showIncreaseBidLimitMessage}
            isDealAmountValid={isDealAmountValid}
            validateDealAmount={validateDealAmount}
            tourInterval={tourInterval}
            onTourIntervalChange={value => {
              this.setState({ tourInterval: value });
            }}
          />

          <Tour step="makeDeal" originalRef={this.original}>
            {({ next, isOriginal, tour }) => (
              <Buttons ref={isOriginal ? this.original : null}>
                <ButtonsInner
                  tourBorder={!isOriginal && tour && tour.borderStyles}
                  tourBorderAnimation={
                    !isOriginal && tour && tour.outside && tour.borderStyles
                  }
                >
                  <CastUp
                    onClick={() => {
                      if (isOriginal) {
                        this._submit("UP");
                      } else {
                        next(["UP", currentBid, tourInterval]);
                      }
                    }}
                    onMouseEnter={() => {
                      this.props.onIntentUpdate("CAST_UP");
                    }}
                    onMouseLeave={() => {
                      this.props.onIntentUpdate("");
                    }}
                  />
                  <CastDown
                    onClick={() => {
                      if (isOriginal) {
                        this._submit("DOWN");
                      } else {
                        next(["DOWN", currentBid, tourInterval]);
                      }
                    }}
                    onMouseEnter={() => {
                      this.props.onIntentUpdate("CAST_DOWN");
                    }}
                    onMouseLeave={() => {
                      this.props.onIntentUpdate("");
                    }}
                  />
                </ButtonsInner>
              </Buttons>
            )}
          </Tour>
        </Wrapper>

        <CollapsibleSection
          Icon={DealsIcon}
          title={<Gettext>Deals</Gettext>}
          isOpenByDefault={true}
        >
          {({ isOpen }) => {
            return (
              <ModalConsumer>
                {({ showModal }) => (
                  <Fade in={isOpen}>
                    {({ style }) => (
                      <CollapsibleContainer style={style}>
                        <Scrollable>
                          <Tour
                            step="chartWithDeals"
                            originalRef={this.originalDeals}
                            zIndex={110}
                          >
                            {({
                              next,
                              isOriginal,
                              isTourActive,
                              isTourMode,
                              tour
                            }) =>
                              !isTourMode ? (
                                <TradeHistory
                                  tradeMode={this.props.tradeMode}
                                  deals={deals}
                                  now={this.props.now}
                                  point={this.props.point}
                                  pointsByStock={this.props.pointsByStock}
                                  isOperationClickable={operation => {
                                    if (
                                      FeatureFlags.OperationCheque &&
                                      operation.profit != null
                                    ) {
                                      return true;
                                    } else {
                                      return (
                                        operation.stock !==
                                        this.props.stock.stock
                                      );
                                    }
                                  }}
                                  onOperationClick={operation => {
                                    if (
                                      FeatureFlags.OperationCheque &&
                                      operation.profit != null
                                    ) {
                                      showModal(OperationCheque, {
                                        type: tradeMode,
                                        id: operation.id,
                                        operation
                                      });
                                    } else {
                                      this.props.dispatch({
                                        type: "SWITCH_TO_STOCK",
                                        payload: operation.stock
                                      });
                                    }
                                  }}
                                  refundClickHandler={
                                    this.props.refundClickHandler
                                  }
                                  refundFetching={this.props.refundFetching}
                                />
                              ) : (
                                <div
                                  ref={this.originalDeals}
                                  style={{ pointerEvents: "none" }}
                                >
                                  <ArtificialTourDeals
                                    mobile={this.props.mobile}
                                  />
                                </div>
                              )
                            }
                          </Tour>
                        </Scrollable>
                        <HistoryButton type="button" onClick={this.openPopup}>
                          <HistoryIcon>
                            <TradingHistoryIcon />
                          </HistoryIcon>
                          <Gettext>Trading history</Gettext>
                        </HistoryButton>

                        {this.state.popupIsOpen && (
                          <HistoryPopup
                            type="widget"
                            themeName={
                              this.props.theme === "light" && process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
                                ? "whitePopup"
                                : "darkPopup"
                            }
                            onClose={this.closePopup}
                          />
                        )}
                      </CollapsibleContainer>
                    )}
                  </Fade>
                )}
              </ModalConsumer>
            );
          }}
        </CollapsibleSection>
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  theme: state.settings.theme
});

export default withFetch(connect(mapStateToProps)(TradeControls));



// WEBPACK FOOTER //
// ./src/components/TradeControls/TradeControls.js